import { useState, useMemo, useCallback, useEffect } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Shield,
  Users,
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  UserCog,
  User,
  Phone,
  Lock,
  Save,
  RefreshCcw,
  MapPin,
  Calendar,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Smartphone,
  QrCode,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  Admin,
  AdminGroup,
  AdminStatus,
  AdminFormData,
  ADMIN_GROUP_CONFIG,
  ADMIN_STATUS_CONFIG,
} from '@/domain/admin/admin.types'
import { AdminService } from '@/domain/admin/admin.service'
import { TwoFactorAuthService } from '@/domain/two-factor-auth/two-factor-auth.service'
import { useAuth } from '@/components/auth'

// Page Size Options
const PAGE_SIZES = [10, 20, 50, 100]

export function AdminsPage() {
  // Auth
  const { sessionToken, user: authUser, logout } = useAuth()
  
  // Data State
  const [admins, setAdmins] = useState<Admin[]>([])
  const [totalAdmins, setTotalAdmins] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  
  // Filter State
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [groupFilter, setGroupFilter] = useState<string>('all')
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  
  // Dialog State
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // 2FA Setup Dialog State
  const [show2FASetupDialog, setShow2FASetupDialog] = useState(false)
  const [twoFASetupData, setTwoFASetupData] = useState<{
    adminId: string
    adminName: string
    qrCode: string
    secret: string
  } | null>(null)
  const [twoFAVerificationCode, setTwoFAVerificationCode] = useState('')
  const [isVerifying2FA, setIsVerifying2FA] = useState(false)
  const [isGeneratingQR, setIsGeneratingQR] = useState(false)
  const [twoFAError, setTwoFAError] = useState<string | null>(null)
  
  // 2FA Config (สำหรับตรวจสอบ require2FAForAdmin)
  const [twoFAConfig, setTwoFAConfig] = useState<{
    enable2FA: boolean
    require2FAForAdmin: boolean
  }>({
    enable2FA: false,
    require2FAForAdmin: false,
  })
  
  // Load 2FA config on mount
  useEffect(() => {
    const load2FAConfig = async () => {
      try {
        const { getAppConfigService } = await import('@/domain/app-config')
        const service = getAppConfigService()
        const config = await service.getAppConfig()
        if (config?.featureFlags) {
          setTwoFAConfig({
            enable2FA: config.featureFlags.enable2FA || false,
            require2FAForAdmin: config.featureFlags.require2FAForAdmin || false,
          })
        }
      } catch (error) {
        console.error('[AdminsPage] Load 2FA config error:', error)
      }
    }
    load2FAConfig()
  }, [])
  
  // Form state
  const [formData, setFormData] = useState<AdminFormData>({
    username: '',
    password: '',
    firstname: '',
    lastname: '',
    admin_tel: '',
    email: '',
    admin_group: AdminGroup.ADMIN,
    status: 'Active',
    twoFactorEnabled: false,
    permissions: [],
  })
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')

  // =========================================
  // Load Data
  // =========================================
  
  const loadAdmins = useCallback(async () => {
    try {
      setIsLoading(true)
      
      const result = await AdminService.findAll({
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
      })
      
      // Filter ที่ client side (เพราะ API อาจไม่รองรับ)
      let filtered = result.admins
      
      if (statusFilter !== 'all') {
        filtered = filtered.filter(a => a.status === statusFilter)
      }
      
      if (groupFilter !== 'all') {
        filtered = filtered.filter(a => a.admin_group === Number(groupFilter))
      }
      
      setAdmins(filtered)
      setTotalAdmins(result.total)
    } catch (error) {
      console.error('Load admins error:', error)
      toast.error('ไม่สามารถโหลดข้อมูลได้')
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, groupFilter])

  useEffect(() => {
    loadAdmins()
  }, [loadAdmins])

  // =========================================
  // Stats (Client-side calculation)
  // =========================================
  
  const stats = useMemo(() => ({
    total: admins.length,
    active: admins.filter(a => a.status === 'Active').length,
    inactive: admins.filter(a => a.status === 'Inactive' || a.status === 'Suspend').length,
    watchout: admins.filter(a => a.status === 'Watchout').length,
  }), [admins])

  // =========================================
  // Pagination
  // =========================================
  
  const totalPages = Math.ceil(totalAdmins / pageSize) || 1

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }, [totalPages])

  const handlePageSizeChange = useCallback((size: string) => {
    setPageSize(Number(size))
    setCurrentPage(1)
  }, [])

  // =========================================
  // Dialog Handlers
  // =========================================
  
  const openCreateDialog = useCallback(() => {
    setFormMode('create')
    setFormData({
      username: '',
      password: '',
      firstname: '',
      lastname: '',
      admin_tel: '',
      email: '',
      admin_group: AdminGroup.ADMIN,
      status: 'Active',
      twoFactorEnabled: false,
      permissions: [],
    })
    setShowCreateDialog(true)
  }, [])

  const openEditDialog = useCallback((admin: Admin) => {
    setFormMode('edit')
    setSelectedAdmin(admin)
    setFormData({
      username: admin.username,
      firstname: admin.firstname,
      lastname: admin.lastname,
      admin_tel: admin.admin_tel || '',
      email: admin.email || '',
      admin_group: admin.admin_group,
      status: admin.status,
      twoFactorEnabled: admin.twoFactorEnabled,
      permissions: admin.permissions || [],
    })
    setShowCreateDialog(true)
  }, [])

  const viewDetail = useCallback((admin: Admin) => {
    setSelectedAdmin(admin)
    setShowDetailDialog(true)
  }, [])

  const openDeleteDialog = useCallback((admin: Admin) => {
    setSelectedAdmin(admin)
    setShowDeleteDialog(true)
  }, [])

  // =========================================
  // CRUD Handlers
  // =========================================
  
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true)
      
      if (formMode === 'create') {
        await AdminService.create(formData)
        toast.success('สร้างผู้ดูแลระบบใหม่เรียบร้อยแล้ว')
      } else if (selectedAdmin) {
        await AdminService.update(selectedAdmin.id, formData)
        toast.success('บันทึกข้อมูลเรียบร้อยแล้ว')
      }
      
      setShowCreateDialog(false)
      loadAdmins() // Reload data
    } catch (error: any) {
      console.error('Save error:', error)
      toast.error(error.message || 'ไม่สามารถบันทึกข้อมูลได้')
    } finally {
      setIsSaving(false)
    }
  }, [formMode, formData, selectedAdmin, loadAdmins])

  const handleDelete = useCallback(async () => {
    if (!selectedAdmin) return
    
    try {
      setIsDeleting(true)
      
      // ใช้ soft delete (set deletedAt + status = Cancel)
      await AdminService.softDelete(selectedAdmin.id)
      
      toast.success('ลบผู้ดูแลระบบเรียบร้อยแล้ว')
      
      setShowDeleteDialog(false)
      loadAdmins() // Reload data
    } catch (error: any) {
      console.error('Delete error:', error)
      toast.error(error.message || 'ไม่สามารถลบข้อมูลได้')
    } finally {
      setIsDeleting(false)
    }
  }, [selectedAdmin, loadAdmins])

  const handleChangeStatus = useCallback(async (admin: Admin, newStatus: AdminStatus) => {
    try {
      await AdminService.changeStatus(admin.id, newStatus)
      toast.success(`เปลี่ยนสถานะเป็น "${ADMIN_STATUS_CONFIG[newStatus].label}" แล้ว`)
      loadAdmins()
    } catch (error: any) {
      toast.error(error.message || 'ไม่สามารถเปลี่ยนสถานะได้')
    }
  }, [loadAdmins])

  // =========================================
  // Render Helpers
  // =========================================
  
  const getGroupBadge = (group: AdminGroup) => {
    const config = ADMIN_GROUP_CONFIG[group]
    const icon = group === AdminGroup.SUPER_ADMIN 
      ? <Crown className="h-3 w-3" />
      : group === AdminGroup.ADMIN 
        ? <Shield className="h-3 w-3" />
        : <UserCog className="h-3 w-3" />
    return (
      <Badge variant="secondary" className={cn("border-0 gap-1", config.color)}>
        {icon} {config.label}
      </Badge>
    )
  }

  const getStatusBadge = (status: AdminStatus) => {
    const config = ADMIN_STATUS_CONFIG[status]
    const icon = status === 'Active' 
      ? <CheckCircle className="h-3 w-3" />
      : status === 'Suspend' 
        ? <Ban className="h-3 w-3" />
        : status === 'Watchout'
          ? <AlertTriangle className="h-3 w-3" />
          : <XCircle className="h-3 w-3" />
    return (
      <Badge variant="secondary" className={cn("border-0 gap-1", config.bgColor, config.color)}>
        {icon} {config.label}
      </Badge>
    )
  }

  // =========================================
  // Render
  // =========================================
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ผู้ดูแลระบบ</h1>
          <p className="text-muted-foreground">บริหารจัดการผู้ดูแลระบบและสิทธิ์การเข้าถึง</p>
        </div>
        <Button className="gap-2" onClick={openCreateDialog}>
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">เพิ่มผู้ดูแลระบบ</span>
          <span className="sm:hidden">เพิ่ม</span>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-green-500/10">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ปกติ</p>
                <p className="text-2xl font-bold text-green-500">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-yellow-500/10">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">เฝ้าระวัง</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.watchout}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-red-500/10">
                <Ban className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ระงับ/ไม่ใช้งาน</p>
                <p className="text-2xl font-bold text-red-500">{stats.inactive}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา ชื่อผู้ใช้, ชื่อ, โทรศัพท์..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1) }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="สถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกสถานะ</SelectItem>
                  <SelectItem value="Active">ปกติ</SelectItem>
                  <SelectItem value="Suspend">ระงับ</SelectItem>
                  <SelectItem value="Watchout">เฝ้าระวัง</SelectItem>
                  <SelectItem value="Inactive">ไม่เคลื่อนไหว</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={groupFilter} onValueChange={(v) => { setGroupFilter(v); setCurrentPage(1) }}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="กลุ่ม" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกกลุ่ม</SelectItem>
                  <SelectItem value="0">Super Admin</SelectItem>
                  <SelectItem value="1">Admin</SelectItem>
                  <SelectItem value="3">Partner</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon" onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
                setGroupFilter('all')
                setCurrentPage(1)
                loadAdmins()
              }}>
                <RefreshCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead className="w-24 text-center">จัดการ</TableHead>
                  <TableHead className="w-24">สถานะ</TableHead>
                  <TableHead>ชื่อผู้ใช้</TableHead>
                  <TableHead>ชื่อ-นามสกุล</TableHead>
                  <TableHead className="hidden md:table-cell">เบอร์โทรศัพท์</TableHead>
                  <TableHead>ประเภท</TableHead>
                  <TableHead className="hidden lg:table-cell text-center">2FA</TableHead>
                  <TableHead className="hidden lg:table-cell">ออนไลน์ล่าสุด</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : admins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
                      ไม่พบข้อมูล
                    </TableCell>
                  </TableRow>
                ) : (
                  admins.map((admin, index) => (
                    <TableRow key={admin.id} className="hover:bg-muted/30">
                      <TableCell className="text-center text-muted-foreground">
                        {(currentPage - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => openEditDialog(admin)}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>แก้ไข</TooltipContent>
                          </Tooltip>
                          
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => viewDetail(admin)}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>ดูรายละเอียด</TooltipContent>
                          </Tooltip>
                          
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={() => openDeleteDialog(admin)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>ลบ</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(admin.status)}</TableCell>
                      <TableCell>
                        <span className="font-medium">{admin.username}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white shrink-0",
                            admin.admin_group === AdminGroup.SUPER_ADMIN 
                              ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                              : 'bg-primary'
                          )}>
                            {admin.firstname.charAt(0)}
                          </div>
                          <span>{admin.firstname} {admin.lastname}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {admin.admin_tel || '-'}
                      </TableCell>
                      <TableCell>{getGroupBadge(admin.admin_group)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-center">
                        {admin.twoFactorEnabled ? (
                          <Badge variant="secondary" className="bg-green-500/10 text-green-500 border-0 gap-1">
                            <Shield className="h-3 w-3" />
                            ON
                          </Badge>
                        ) : twoFAConfig.require2FAForAdmin && (admin.admin_group === AdminGroup.ADMIN || admin.admin_group === AdminGroup.SUPER_ADMIN) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" className="bg-amber-500/10 text-amber-500 border-0 gap-1 cursor-help">
                                <AlertTriangle className="h-3 w-3" />
                                ต้องตั้งค่า
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>ระบบบังคับให้ Admin ต้องเปิดใช้งาน 2FA</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Badge variant="secondary" className="bg-muted text-muted-foreground border-0">
                            OFF
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {admin.lastLogin ? (
                          <div className="text-sm">
                            <div>{new Date(admin.lastLogin).toLocaleDateString('th-TH')}</div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(admin.lastLogin).toLocaleTimeString('th-TH')}
                            </div>
                          </div>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>แสดง</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map(size => (
                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>รายการ จากทั้งหมด {totalAdmins} รายการ</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <span className="px-3 text-sm">
                หน้า {currentPage} / {totalPages}
              </span>
              
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* =========================================
          Create/Edit Dialog
          ========================================= */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-primary" />
              {formMode === 'create' ? 'เพิ่มผู้ดูแลระบบใหม่' : 'แก้ไขข้อมูลผู้ดูแลระบบ'}
            </DialogTitle>
            <DialogDescription>
              {formMode === 'create' 
                ? 'กรอกข้อมูลเพื่อสร้างบัญชีผู้ดูแลระบบใหม่'
                : 'แก้ไขข้อมูลผู้ดูแลระบบ'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* ชื่อ-นามสกุล */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ชื่อ <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="ชื่อ"
                  value={formData.firstname}
                  onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>นามสกุล <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="นามสกุล"
                  value={formData.lastname}
                  onChange={(e) => setFormData({...formData, lastname: e.target.value})}
                />
              </div>
            </div>

            {/* เบอร์โทร */}
            <div className="space-y-2">
              <Label>เบอร์โทรศัพท์</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="08X-XXX-XXXX"
                  className="pl-10"
                  value={formData.admin_tel}
                  onChange={(e) => setFormData({...formData, admin_tel: e.target.value})}
                />
              </div>
            </div>

            <Separator />

            {/* ชื่อผู้ใช้ */}
            <div className="space-y-2">
              <Label>ชื่อผู้ใช้ (สำหรับเข้าระบบ) <span className="text-destructive">*</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="username"
                  className="pl-10"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  disabled={formMode === 'edit'}
                />
              </div>
              {formMode === 'edit' && (
                <p className="text-xs text-muted-foreground">ไม่สามารถแก้ไขชื่อผู้ใช้ได้</p>
              )}
            </div>

            {/* รหัสผ่าน */}
            <div className="space-y-2">
              <Label>
                รหัสผ่าน {formMode === 'create' && <span className="text-destructive">*</span>}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder={formMode === 'edit' ? 'ใส่รหัสผ่านใหม่ถ้าต้องการเปลี่ยน' : 'กรอกรหัสผ่าน (อย่างน้อย 4 ตัว)'}
                  className="pl-10"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
              </div>
              {formMode === 'edit' && (
                <p className="text-xs text-red-500">*** หากต้องการแก้ไขรหัสผ่าน สามารถใส่รหัสผ่านใหม่แล้วกดบันทึกได้เลย</p>
              )}
            </div>

            <Separator />

            {/* กลุ่ม & สถานะ */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>กลุ่มผู้ดูแลระบบ <span className="text-destructive">*</span></Label>
                <Select 
                  value={String(formData.admin_group)}
                  onValueChange={(v) => setFormData({...formData, admin_group: Number(v) as AdminGroup})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกกลุ่ม" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-amber-500" />
                        Super Admin
                      </div>
                    </SelectItem>
                    <SelectItem value="1">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-500" />
                        Admin
                      </div>
                    </SelectItem>
                    <SelectItem value="3">
                      <div className="flex items-center gap-2">
                        <UserCog className="h-4 w-4 text-blue-500" />
                        Partner
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>สถานะ</Label>
                <Select 
                  value={formData.status}
                  onValueChange={(v) => setFormData({...formData, status: v as AdminStatus})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกสถานะ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">ปกติ</SelectItem>
                    <SelectItem value="Suspend">ระงับ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 2FA */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div>
                <Label>เปิดใช้ 2FA</Label>
                <p className="text-xs text-muted-foreground">บังคับใช้ยืนยันตัวตน 2 ขั้นตอน</p>
              </div>
              <Switch 
                checked={formData.twoFactorEnabled}
                onCheckedChange={(checked) => setFormData({...formData, twoFactorEnabled: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={isSaving}>
              ยกเลิก
            </Button>
            <Button 
              className="gap-2"
              disabled={!formData.firstname || !formData.lastname || !formData.username || (formMode === 'create' && !formData.password) || isSaving}
              onClick={handleSave}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {formMode === 'create' ? 'สร้างผู้ดูแล' : 'บันทึก'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =========================================
          Detail Dialog
          ========================================= */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          {selectedAdmin && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className={cn(
                    "h-12 w-12 rounded-full flex items-center justify-center font-semibold text-xl text-white",
                    selectedAdmin.admin_group === AdminGroup.SUPER_ADMIN 
                      ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                      : 'bg-primary'
                  )}>
                    {selectedAdmin.firstname.charAt(0)}
                  </div>
                  <div>
                    <p>{selectedAdmin.firstname} {selectedAdmin.lastname}</p>
                    <p className="text-sm font-normal text-muted-foreground">@{selectedAdmin.username}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {getGroupBadge(selectedAdmin.admin_group)}
                  {getStatusBadge(selectedAdmin.status)}
                  {selectedAdmin.twoFactorEnabled ? (
                    <Badge variant="secondary" className="bg-green-500/10 text-green-500 border-0 gap-1">
                      <Shield className="h-3 w-3" /> 2FA เปิดใช้งาน
                    </Badge>
                  ) : twoFAConfig.require2FAForAdmin && (selectedAdmin.admin_group === AdminGroup.ADMIN || selectedAdmin.admin_group === AdminGroup.SUPER_ADMIN) ? (
                    <Badge variant="secondary" className="bg-amber-500/10 text-amber-500 border-0 gap-1">
                      <AlertTriangle className="h-3 w-3" /> ต้องตั้งค่า 2FA
                    </Badge>
                  ) : null}
                </div>
                
                {/* Warning สำหรับ Admin ที่ต้อง setup 2FA */}
                {!selectedAdmin.twoFactorEnabled && twoFAConfig.require2FAForAdmin && (selectedAdmin.admin_group === AdminGroup.ADMIN || selectedAdmin.admin_group === AdminGroup.SUPER_ADMIN) && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          ต้องตั้งค่า 2FA
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          ระบบบังคับให้ Admin ต้องเปิดใช้งาน 2FA เพื่อความปลอดภัย
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/30">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" /> โทรศัพท์
                    </p>
                    <p className="font-medium">{selectedAdmin.admin_tel || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> สร้างเมื่อ
                    </p>
                    <p className="font-medium">
                      {selectedAdmin.createdAt 
                        ? new Date(selectedAdmin.createdAt).toLocaleDateString('th-TH')
                        : '-'
                      }
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> เข้าสู่ระบบล่าสุด
                    </p>
                    <p className="font-medium">
                      {selectedAdmin.lastLogin 
                        ? new Date(selectedAdmin.lastLogin).toLocaleString('th-TH')
                        : '-'
                      }
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> IP Address
                    </p>
                    <p className="font-medium font-mono text-sm">{selectedAdmin.lastLoginIp || '-'}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  {/* ✅ Setup 2FA Button (สำหรับ Super Admin) */}
                  {!selectedAdmin.twoFactorEnabled && (
                    <Button
                      variant="default"
                      className="gap-2 bg-primary hover:bg-primary/90"
                      onClick={async () => {
                        if (!sessionToken) {
                          toast.error('ไม่พบ Session Token')
                          return
                        }
                        try {
                          setIsGeneratingQR(true)
                          setTwoFAError(null)
                          // สร้าง QR Code สำหรับ Admin นี้
                          const result = await TwoFactorAuthService.generateQRCode(sessionToken, selectedAdmin.id)
                          if (result.success && result.qrCode) {
                            // เปิด dialog แสดง QR Code
                            setShow2FASetupDialog(true)
                            setTwoFASetupData({
                              adminId: selectedAdmin.id,
                              adminName: `${selectedAdmin.firstname} ${selectedAdmin.lastname}`,
                              qrCode: result.qrCode,
                              secret: result.secret || '',
                            })
                            setShowDetailDialog(false) // ปิด Detail Dialog
                          } else {
                            setTwoFAError(result.error || 'ไม่สามารถสร้าง QR Code ได้')
                            toast.error(result.error || 'ไม่สามารถสร้าง QR Code ได้')
                          }
                        } catch (error: any) {
                          setTwoFAError(error.message || 'เกิดข้อผิดพลาด')
                          toast.error(error.message || 'เกิดข้อผิดพลาด')
                        } finally {
                          setIsGeneratingQR(false)
                        }
                      }}
                      disabled={isGeneratingQR}
                    >
                      {isGeneratingQR ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          กำลังสร้าง QR Code...
                        </>
                      ) : (
                        <>
                          <QrCode className="h-4 w-4" />
                          Setup 2FA
                        </>
                      )}
                    </Button>
                  )}
                  <Button variant="outline" className="flex-1 gap-2" onClick={() => {
                    setShowDetailDialog(false)
                    openEditDialog(selectedAdmin)
                  }}>
                    <Edit className="h-4 w-4" />
                    แก้ไข
                  </Button>
                  {selectedAdmin.status === 'Active' ? (
                    <Button 
                      variant="outline" 
                      className="flex-1 gap-2 text-destructive hover:text-destructive"
                      onClick={() => handleChangeStatus(selectedAdmin, 'Suspend')}
                    >
                      <Ban className="h-4 w-4" />
                      ระงับ
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="flex-1 gap-2 text-green-500 hover:text-green-500"
                      onClick={() => handleChangeStatus(selectedAdmin, 'Active')}
                    >
                      <CheckCircle className="h-4 w-4" />
                      เปิดใช้งาน
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* =========================================
          Delete Confirmation Dialog
          ========================================= */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              ยืนยันการลบ
            </AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบผู้ดูแลระบบ <strong>"{selectedAdmin?.firstname} {selectedAdmin?.lastname}"</strong> ใช่หรือไม่?
              <br />
              <span className="text-muted-foreground text-sm">
                (ข้อมูลจะถูกเปลี่ยนสถานะเป็น "ยกเลิก" แต่ยังอยู่ในระบบ)
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* =========================================
          2FA Setup Dialog (สำหรับ Super Admin)
          ========================================= */}
      <Dialog open={show2FASetupDialog} onOpenChange={(open) => {
        setShow2FASetupDialog(open)
        if (!open) {
          // Reset state when closing
          setTwoFASetupData(null)
          setTwoFAVerificationCode('')
          setTwoFAError(null)
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Setup 2FA สำหรับ {twoFASetupData?.adminName}
            </DialogTitle>
            <DialogDescription>
              สร้าง QR Code สำหรับ Admin เพื่อสแกนด้วยแอป Authenticator
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {twoFASetupData ? (
              <div className="space-y-4">
                {/* Step 1: Scan QR Code */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                      1
                    </div>
                    <Label className="text-base">สแกน QR Code</Label>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30 text-center border-2 border-dashed border-primary/20">
                    <img 
                      src={twoFASetupData.qrCode} 
                      alt="2FA QR Code" 
                      className="h-48 w-48 mx-auto mb-3 rounded-lg border-2 border-border"
                    />
                    <p className="text-sm font-medium mb-1">
                      เปิดแอป Authenticator และสแกน QR Code
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      รองรับ Google Authenticator, Authy, Microsoft Authenticator
                    </p>
                    {twoFASetupData.secret && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-2">หรือกรอก Manual Entry Key:</p>
                        <div className="p-2 rounded bg-background border border-border">
                          <p className="text-xs font-mono break-all text-center">
                            {twoFASetupData.secret}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 2: Verify Code */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                      2
                    </div>
                    <Label className="text-base">ยืนยันรหัส</Label>
                  </div>
                  <div className="space-y-2">
                    <Input 
                      placeholder="000000" 
                      maxLength={6} 
                      className="text-center text-lg tracking-widest font-mono"
                      value={twoFAVerificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                        setTwoFAVerificationCode(value)
                        setTwoFAError(null)
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      กรอกรหัส 6 หลักจากแอป Authenticator เพื่อยืนยันการเปิดใช้งาน
                    </p>
                  </div>
                </div>

                {/* Error Message */}
                {twoFAError && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                    {twoFAError}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-2" />
                <p className="text-sm text-muted-foreground">กำลังสร้าง QR Code...</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShow2FASetupDialog(false)
                setTwoFASetupData(null)
                setTwoFAVerificationCode('')
                setTwoFAError(null)
              }}
              disabled={isVerifying2FA}
            >
              ยกเลิก
            </Button>
            <Button 
              disabled={!twoFASetupData || twoFAVerificationCode.length !== 6 || isVerifying2FA || !sessionToken}
              onClick={async () => {
                if (!sessionToken || !twoFASetupData) return
                try {
                  setIsVerifying2FA(true)
                  setTwoFAError(null)
                  const result = await TwoFactorAuthService.verifyAndEnable(
                    sessionToken,
                    twoFASetupData.adminId,
                    twoFAVerificationCode
                  )
                  if (result.success) {
                    // ✅ ตรวจสอบว่าเป็น admin คนเดียวกันหรือไม่ (enable 2FA ให้ตัวเอง)
                    const isEnablingForSelf = authUser?.id === twoFASetupData.adminId
                    
                    toast.success('เปิดใช้งาน 2FA สำเร็จ', {
                      description: isEnablingForSelf 
                        ? '2FA ถูกเปิดใช้งานแล้ว ระบบจะออกจากระบบเพื่อให้ทดสอบ 2FA'
                        : `2FA ถูกเปิดใช้งานสำหรับ ${twoFASetupData.adminName} แล้ว`,
                    })
                    
                    setShow2FASetupDialog(false)
                    setTwoFASetupData(null)
                    setTwoFAVerificationCode('')
                    setTwoFAError(null)
                    // Reload admins
                    await loadAdmins()
                    
                    // ✅ ถ้า enable 2FA ให้ตัวเอง → บังคับ logout เพื่อให้ทดลอง
                    if (isEnablingForSelf && logout) {
                      setTimeout(async () => {
                        toast.info('กำลังออกจากระบบ...', {
                          description: 'กรุณาเข้าสู่ระบบอีกครั้งเพื่อทดสอบ 2FA',
                          duration: 3000,
                        })
                        
                        // Logout หลังจาก 2 วินาที
                        setTimeout(async () => {
                          await logout()
                        }, 2000)
                      }, 500)
                    }
                  } else {
                    const errorMsg = result.error || 'รหัสยืนยันไม่ถูกต้อง'
                    setTwoFAError(errorMsg)
                    toast.error(errorMsg)
                  }
                } catch (error: any) {
                  const errorMsg = error.message || 'เกิดข้อผิดพลาด'
                  setTwoFAError(errorMsg)
                  toast.error(errorMsg)
                } finally {
                  setIsVerifying2FA(false)
                }
              }}
              className="gap-2"
            >
              {isVerifying2FA ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  กำลังยืนยัน...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  ยืนยันและเปิดใช้งาน
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
